import {useSyncExternalStore} from 'react'

import {BSKY_APP_HOST} from '#/lib/strings/url-helpers'
import type {
  FamilyLinkDirection,
  FamilyLinkPreview,
  FamilyLinks,
} from '#/state/queries/family'

/**
 * Demo mode: a pure front-end simulation of the family feature so the whole
 * flow (generate code → QR → share via chat → invite card in a DM → accept →
 * linked members) can be seen and clicked through before the family backend is
 * deployed. Nothing here touches the network; every simulated response is
 * generated locally and consumed by the same hooks/components the real backend
 * drives.
 *
 * The store is a tiny module-level pub/sub (no provider needed) so the react-
 * query hooks in state/queries/family.ts can read it from outside React.
 */

export type DemoFamilyRole = 'learner' | 'parent'

export type DemoFamilyPeer = {
  did: string
  handle: string
  displayName: string
}

/** The fake parent used in the learner scenario. */
export const DEMO_PARENT: DemoFamilyPeer = {
  did: 'did:demo:family:sarah',
  handle: 'sarah.family.demo',
  displayName: 'Sarah Chen',
}

/** The fake child used in the parent scenario. */
export const DEMO_CHILD: DemoFamilyPeer = {
  did: 'did:demo:family:leo',
  handle: 'leo.family.demo',
  displayName: 'Leo Chen',
}

/** Extra member of the simulated family group chat. */
export const DEMO_GROUP_MEMBER: DemoFamilyPeer = {
  did: 'did:demo:family:emma',
  handle: 'emma.family.demo',
  displayName: 'Emma Laurent',
}

export const DEMO_CONVO_ID = 'demo-family-convo'

export type DemoFamilyState = {
  enabled: boolean
  /** Which side of the family we are simulating. */
  role: DemoFamilyRole
  /** Code generated by "us" in this demo session (our own invite). */
  code: string | null
  /** Whether the invite was "sent" to the demo peer in chat. */
  sentToChat: boolean
  /** Whether the demo peer accepted the invitation. */
  accepted: boolean
  /** Whether the current invite has expired (simulates the 10-min TTL). */
  expired: boolean
  /**
   * Master family switch. OFF disables every family surface: no incoming
   * invites, no Hub CTA, and the Family screen shows an explanation.
   * Can't be turned OFF while a family link exists.
   */
  familyEnabled: boolean
  /** When WE declined the demo peer's invitation: their chosen cause label. */
  declinedReason: string | null
  /** Sender-view simulation: the demo peer declined OUR invitation w/ cause. */
  peerDeclinedCause: string | null
}

const STORAGE_KEY = 'taderok.demoFamily.v1'

function loadInitialState(): DemoFamilyState {
  const fallback: DemoFamilyState = {
    enabled: false,
    role: 'learner',
    code: null,
    sentToChat: false,
    accepted: false,
    expired: false,
    familyEnabled: true,
    declinedReason: null,
    peerDeclinedCause: null,
  }
  // Persist demo mode + scenario across full page reloads (web). Native has
  // no localStorage, so the in-memory default is used there.
  if (typeof localStorage === 'undefined') return fallback
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as Partial<DemoFamilyState>
    return {...fallback, ...parsed}
  } catch {
    return fallback
  }
}

let state: DemoFamilyState = loadInitialState()
const listeners = new Set<() => void>()

export function getDemoFamilyState(): DemoFamilyState {
  return state
}

export function subscribeDemoFamily(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function setState(partial: Partial<DemoFamilyState>) {
  state = {...state, ...partial}
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch {
      // Storage may be unavailable (private mode) — demo still works in memory.
    }
  }
  listeners.forEach(listener => listener())
}

/** React hook for components. */
export function useDemoFamily(): DemoFamilyState {
  return useSyncExternalStore(subscribeDemoFamily, getDemoFamilyState)
}

// -- actions ----------------------------------------------------------------

export function enableDemo() {
  setState({
    enabled: true,
    role: 'learner',
    code: null,
    sentToChat: false,
    accepted: false,
    expired: false,
    declinedReason: null,
    peerDeclinedCause: null,
  })
}

export function disableDemo() {
  setState({enabled: false, code: null, sentToChat: false, accepted: false, declinedReason: null, peerDeclinedCause: null})
}

export function setDemoRole(role: DemoFamilyRole) {
  // No-op when the role didn't change: SegmentedControl fires onChange once
  // on mount (layout init), which must NOT wipe a code/accept that was
  // persisted across a page reload. Only switching sides resets the
  // scenario.
  if (state.role === role) return
  setState({
    role,
    code: null,
    sentToChat: false,
    accepted: false,
    declinedReason: null,
    peerDeclinedCause: null,
  })
}

export function resetDemoScenario() {
  setState({code: null, sentToChat: false, accepted: false, declinedReason: null, peerDeclinedCause: null})
}

/** Marks the current invite as expired (past its 10-minute TTL). */
export function setDemoExpired(expired: boolean) {
  setState({expired})
}

/**
 * Master family switch. Turning OFF is blocked while a family link exists
 * (accepted/linked members must be unlinked first).
 * @returns true if the switch was applied, false when blocked.
 */
export function setFamilyEnabled(enabled: boolean): boolean {
  if (!enabled && state.accepted) return false
  if (!enabled) {
    // Only clear our own invitation state — never a linked member silently.
    setState({familyEnabled: false, code: null, sentToChat: false})
  } else {
    setState({familyEnabled: true})
  }
  return true
}

/** WE declined the demo peer's invitation (receiver side), with the cause. */
export function declineDemoInvite(reason: string) {
  setState({declinedReason: reason, peerDeclinedCause: null, accepted: false})
}

/** Sender-side simulation: the demo peer declined OUR invitation. */
export function setPeerDeclined(cause: string | null) {
  setState({peerDeclinedCause: cause, accepted: false})
}

/** Generates a fresh demo code and remembers it as "our" invite. */
export function generateDemoCode(): string {
  const code = `DEMO-${Math.random().toString(36).slice(2, 8).toUpperCase()}`
  setState({code})
  return code
}

export function sendDemoInviteToChat() {
  setState({sentToChat: true})
}

export function acceptDemoInvite() {
  setState({accepted: true})
}

export function revokeDemoLink() {
  setState({accepted: false})
}

// -- helpers ----------------------------------------------------------------

/** The demo peer for the current scenario (the other side of the link). */
export function demoPeer(role: DemoFamilyRole): DemoFamilyPeer {
  return role === 'learner' ? DEMO_PARENT : DEMO_CHILD
}

export function demoInviteLink(code: string): string {
  return `${BSKY_APP_HOST}/family/${code}`
}

function demoCodeExpiry(): {createdAt: string; expiresAt: string} {
  const now = Date.now()
  const expired = getDemoFamilyState().expired
  if (expired) {
    // Simulate an invite past its 10-minute TTL.
    return {
      createdAt: new Date(now - 20 * 60_000).toISOString(),
      expiresAt: new Date(now - 10 * 60_000).toISOString(),
    }
  }
  return {
    createdAt: new Date(now - 60_000).toISOString(),
    expiresAt: new Date(now + 10 * 60_000).toISOString(),
  }
}

/**
 * Simulated getFamilyLinkPreview response. Matches the real shape so the
 * invite landing screen, the DM card and the Family screen all render with
 * zero changes.
 *
 * - Our own code → the card shows us as the initiator (role from the current
 *   scenario), exactly what the recipient would see.
 * - Anything else (e.g. the demo peer's code, or any code pasted while demo
 *   mode is on) → the card shows the demo peer as the initiator.
 */
export function demoFamilyPreview(
  code: string,
  me: {did?: string; handle?: string} | undefined,
): FamilyLinkPreview | undefined {
  const s = getDemoFamilyState()
  if (!s.enabled) return undefined

  const expiry = demoCodeExpiry()
  if (s.code && code === s.code) {
    const did = me?.did ?? 'did:demo:family:me'
    const handle = me?.handle ?? 'me.family.demo'
    return {
      initiatorDid: did,
      initiatorRole: s.role === 'learner' ? 'child' : 'parent',
      createdAt: expiry.createdAt,
      expiresAt: expiry.expiresAt,
      initiator: {did, handle, displayName: handle},
    }
  }

  const peer = demoPeer(s.role)
  return {
    initiatorDid: peer.did,
    initiatorRole: s.role === 'learner' ? 'parent' : 'child',
    createdAt: expiry.createdAt,
    expiresAt: expiry.expiresAt,
    initiator: {
      did: peer.did,
      handle: peer.handle,
      displayName: peer.displayName,
    },
  }
}

/** Simulated getFamilyLinks response for the current scenario. */
export function demoFamilyLinks(): FamilyLinks {
  const s = getDemoFamilyState()
  if (!s.enabled || !s.accepted) return {asParent: [], asChild: []}
  const createdAt = new Date(Date.now() - 5 * 60_000).toISOString()
  if (s.role === 'learner') {
    return {asParent: [], asChild: [{peerDid: DEMO_PARENT.did, createdAt}]}
  }
  return {asParent: [{peerDid: DEMO_CHILD.did, createdAt}], asChild: []}
}

/** Simulated confirmFamilyLink response. */
export function demoFamilyConfirmResult(
  myDid: string | undefined,
): {parentDid: string; childDid: string; createdAt: string} {
  const s = getDemoFamilyState()
  const createdAt = new Date().toISOString()
  if (s.role === 'learner') {
    return {parentDid: DEMO_PARENT.did, childDid: myDid ?? '', createdAt}
  }
  return {parentDid: myDid ?? '', childDid: DEMO_CHILD.did, createdAt}
}

/** The initiator role our generated code claims in the current scenario. */
export function demoInitiatorRole(): FamilyLinkDirection {
  return getDemoFamilyState().role === 'learner' ? 'child' : 'parent'
}

/**
 * Simulated resolveFamilyLinkRequest response — powers the "Enter a code"
 * panel in demo mode: any code resolves to the current scenario's initiator.
 */
export function demoResolveRequest(
  code: string,
  me: {did?: string; handle?: string} | undefined,
): {initiatorDid: string; initiatorRole: FamilyLinkDirection; createdAt: string; expiresAt: string} | undefined {
  const preview = demoFamilyPreview(code, me)
  if (!preview) return undefined
  return {
    initiatorDid: preview.initiatorDid,
    initiatorRole: preview.initiatorRole,
    createdAt: preview.createdAt,
    expiresAt: preview.expiresAt,
  }
}